import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAutoSaveCoordinationOptional } from '@/components/proposals/shared/useAutoSaveCoordination';
import { logger } from '@/lib/logger';

import type {
  ClarifyingAnswer,
  ClarifyingQuestions,
  ProposalDetail,
} from '../core/types';
import { proposalQueryKeys } from '../query/keys';
import {
  getClarifyingAnswersRegistrationId,
  useClarifyingAnswersAutoSave,
} from './useClarifyingAnswersAutoSave';

// ── Helpers ───────────────────────────────────────────────────────────────────

function seedFromQuestions(
  clarifyingQuestions: ClarifyingQuestions | null
): Record<string, ClarifyingAnswer> {
  const answers = clarifyingQuestions?.answers ?? [];
  const result: Record<string, ClarifyingAnswer> = {};
  for (const answer of answers) {
    result[answer.questionId] = answer;
  }
  return result;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UseClarifyingAnswersParams {
  orgId: number;
  proposalId: number;
  clarifyingQuestions: ClarifyingQuestions | null;
  editable: boolean;
  onSubmitAnswers?: () => void;
}

export interface UseClarifyingAnswersResult {
  answers: Record<string, ClarifyingAnswer>;
  selectOption: (questionId: string, optionId: string) => void;
  toggleOption: (questionId: string, optionId: string) => void;
  setOther: (questionId: string, text: string) => void;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  /** Resolves true when the answers were saved (and onSubmitAnswers ran);
   * false when the save failed or the hook wasn't submittable. Callers use
   * this to dismiss UI only on success. */
  submitAnswers: () => Promise<boolean>;
  isSubmitting: boolean;
  /** True when the current answers were submitted to the agent and unchanged
   * since — seeded from the envelope's answers_submitted_at (durable across
   * reloads), cleared the moment any answer is edited, set again on a
   * successful submit. Callers use it to disable resubmission of an
   * identical answer set. */
  hasSubmitted: boolean;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useClarifyingAnswers({
  orgId,
  proposalId,
  clarifyingQuestions,
  editable,
  onSubmitAnswers,
}: UseClarifyingAnswersParams): UseClarifyingAnswersResult {
  const [answers, setAnswers] = useState<Record<string, ClarifyingAnswer>>(() =>
    seedFromQuestions(clarifyingQuestions)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Durable "these exact answers were sent" marker, seeded from the envelope
  // (survives reloads) and maintained locally: edits clear it (the autosave
  // they trigger also clears the server stamp), a successful submit sets it.
  const [hasSubmitted, setHasSubmitted] = useState(() =>
    Boolean(clarifyingQuestions?.answers_submitted_at)
  );

  const queryClient = useQueryClient();

  // Editing a submitted gating answer must re-lock the actions immediately, but
  // the server stamp (answers_submitted_at) only clears once the debounced
  // autosave lands (~2s + request). The lock derivation reads that stamp off
  // the proposal envelope, so until then approval — including the keyboard
  // path that flushes-then-approves — stays wrongly unlocked against stale
  // state. Clear the stamp optimistically the moment an answer changes so the
  // lock engages in the same commit; the autosave (submitted: false) then
  // persists the same clearing. No-ops when there is no stamp to clear.
  const clearSubmittedStamp = useCallback(() => {
    queryClient.setQueryData<ProposalDetail>(
      proposalQueryKeys.detail(orgId, proposalId),
      prev => {
        const cq = prev?.clarifying_questions;
        if (!prev || !cq || !cq.answers_submitted_at) {
          return prev;
        }
        return {
          ...prev,
          clarifying_questions: { ...cq, answers_submitted_at: null },
        };
      }
    );
  }, [queryClient, orgId, proposalId]);

  // getAnswers reads current answers at call time for the autosave hook (F23).
  const getAnswers = useCallback(() => Object.values(answers), [answers]);

  const coordination = useAutoSaveCoordinationOptional();

  const { scheduleSave, submit, saveState, cancelPendingSave } =
    useClarifyingAnswersAutoSave({
      orgId: String(orgId),
      proposalId: String(proposalId),
      getAnswers,
      editable,
    });

  // Reset answers when the proposal or its question set changes (F11). The reset
  // key includes proposalId because answers are proposal-scoped: switching to a
  // different proposal that happens to share a question_set_id must still re-seed
  // so one proposal's answers never display or save against another. The pending
  // debounce is cancelled first so no stale save from the previous proposal /
  // question set fires against the newly seeded answers.
  const resetKeyRef = useRef(
    `${proposalId}:${clarifyingQuestions?.question_set_id ?? ''}`
  );
  useEffect(() => {
    const nextKey = `${proposalId}:${clarifyingQuestions?.question_set_id ?? ''}`;
    if (nextKey !== resetKeyRef.current) {
      resetKeyRef.current = nextKey;
      cancelPendingSave();
      setAnswers(seedFromQuestions(clarifyingQuestions));
      setHasSubmitted(Boolean(clarifyingQuestions?.answers_submitted_at));
    }
  }, [proposalId, clarifyingQuestions, cancelPendingSave]);

  // ── Answer setters ────────────────────────────────────────────────────────

  const selectOption = useCallback(
    (questionId: string, optionId: string) => {
      const next: ClarifyingAnswer = {
        questionId,
        kind: 'option',
        optionIds: [optionId],
      };
      setAnswers((prev: Record<string, ClarifyingAnswer>) => ({
        ...prev,
        [questionId]: next,
      }));
      setHasSubmitted(false);
      clearSubmittedStamp();
      scheduleSave();
    },
    [scheduleSave, clearSubmittedStamp]
  );

  const toggleOption = useCallback(
    (questionId: string, optionId: string) => {
      setAnswers((prev: Record<string, ClarifyingAnswer>) => {
        const existing = prev[questionId];
        const currentIds =
          existing?.kind === 'option' ? (existing.optionIds ?? []) : [];
        const nextIds = currentIds.includes(optionId)
          ? currentIds.filter((id: string) => id !== optionId)
          : [...currentIds, optionId];
        const next: ClarifyingAnswer = {
          questionId,
          kind: 'option',
          optionIds: nextIds,
        };
        return { ...prev, [questionId]: next };
      });
      setHasSubmitted(false);
      clearSubmittedStamp();
      scheduleSave();
    },
    [scheduleSave, clearSubmittedStamp]
  );

  const setOther = useCallback(
    (questionId: string, text: string) => {
      const next: ClarifyingAnswer = { questionId, kind: 'other', text };
      setAnswers((prev: Record<string, ClarifyingAnswer>) => ({
        ...prev,
        [questionId]: next,
      }));
      setHasSubmitted(false);
      clearSubmittedStamp();
      scheduleSave();
    },
    [scheduleSave, clearSubmittedStamp]
  );

  // ── Submit (F8/F9/F10) ────────────────────────────────────────────────────

  const submitAnswers = useCallback(async (): Promise<boolean> => {
    if (!editable || isSubmitting) {
      return false;
    }
    setIsSubmitting(true);
    try {
      // Flush any unsaved form typing in OTHER registered editors (e.g. an
      // ask_user_for_info answer mid-debounce) so their update_payload lands
      // before the refine turn that this submit kicks off. This hook's own
      // answers are excluded — submit() below already flushes and stamps
      // them, so including them here would double-save.
      const flushResult = await coordination
        ?.flushAllAndWait({
          excludeIds: [getClarifyingAnswersRegistrationId(String(proposalId))],
        })
        .catch(err => {
          logger.warn('Autosave flush before clarifying submit failed', {
            component: 'proposals',
            proposalId,
          });
          return {
            success: false,
            errors: [err instanceof Error ? err : new Error(String(err))],
          };
        });

      // A failed or timed-out flush means another editor's unsaved answer may
      // not have reached the server yet. Proceeding would run the refine turn
      // against a stale payload, so abort and leave the unsaved state in
      // place rather than submit.
      if (flushResult && !flushResult.success) {
        logger.warn(
          'Aborting clarifying submit: autosave flush did not complete',
          { component: 'proposals', proposalId }
        );
        return false;
      }
      // submit() saves with submitted: true (stamping answers_submitted_at
      // server-side) and rejects when the save fails, so onSubmitAnswers only
      // runs on success. saveState is set to 'error' inside save() for the UI
      // to surface; we still log here so the failure is diagnosable.
      await submit();
      setHasSubmitted(true);
      onSubmitAnswers?.();
      return true;
    } catch (err) {
      logger.error(
        'Failed to save clarifying answers on submit',
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'proposals',
          proposalId,
          questionSetId: clarifyingQuestions?.question_set_id,
        }
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    editable,
    isSubmitting,
    coordination,
    submit,
    onSubmitAnswers,
    proposalId,
    clarifyingQuestions?.question_set_id,
  ]);

  return {
    answers,
    selectOption,
    toggleOption,
    setOther,
    saveState,
    submitAnswers,
    isSubmitting,
    hasSubmitted,
  };
}
